/**
 * Phase 41 Plan 41-17 (Task 2) — TaskDetailSlideOver.
 *
 * Right-anchored slide-over panel that REPLACES the centered TaskDetailModal
 * (Phase 24 / Phase 41-08). Per CONTEXT amendment 2026-05-12 D-TaskSlideOver:
 * "Pure layout + interaction surface swap. All existing data hooks rewire
 * 1:1; no backend changes; no data shape changes."
 *
 * Hooks rewired 1:1 from TaskDetailModal:
 *   - useUpdateTask / useDeleteTask / useTaskMutation
 *   - useMilestones / useAssignTaskMilestone
 *   - CommentList / CommentComposer
 *   - useExternalCurrentUser / useAuth
 *
 * Sections (top → bottom, per `Task detail _ slide-over panel.html` ref):
 *   1. Header — task code + name (or inline-edit input) + Edit pencil + Close X
 *   2. Details — description, indicator chip, milestone field, budget grid
 *   3. SLA timer (Phase 41-08) — SlaTaskCard mounts when projectTaskTypeId
 *      === SLA_TASK_TYPE_ID (CONTEXT D-4 uniform density preserved).
 *   4. Acceptance criteria — currently delegated to description field; explicit
 *      placeholder section reserved (TODO: dedicated `acceptanceCriteria`
 *      column on the Task model is a future-phase concern).
 *   5. Time — "Xh logged of Yh estimate" + % over estimate badge.
 *      Quick task 260512-p6d (2026-05-12): the Start timer / Log time
 *      buttons + the "Time entries" placeholder list were stripped because
 *      both rendered as confusing disabled stubs. Re-introduce them when
 *      the task-timer + per-task time-entries hooks ship (v7.1 backlog).
 *   6. Comments — CommentList + CommentComposer (1:1 from TaskDetailModal).
 *   7. Activity — TaskActivityTimeline (read-only audit feed; moved BELOW
 *      Comments per UAT 2026-05-22 batch 2 — conversation is primary,
 *      audit is secondary).
 *   8. Attachments — comment-attachment workflow exists; per-task standalone
 *      attachment hook does NOT, so this section ships as a disabled
 *      "Coming soon" placeholder with a TODO marker.
 *
 * A11y / interaction:
 *   - Focus trap (inline implementation; T-41-V3-05 mitigation).
 *   - Esc key closes via onClose; backdrop click closes via onClose.
 *   - Edit/archive-confirm/delete-confirm modes preserve TaskDetailModal's
 *     modal-vs-cancel branching: Esc reverts to view mode rather than
 *     closing the panel.
 *   - First focusable element receives focus on open.
 *
 * SECURITY (T-41-V3-04):
 *   - Task name + description rendered through MarkdownBody (sanitized).
 *   - Comment body rendered through CommentList → CommentBubble → MarkdownBody.
 *   - Zero `dangerouslySetInnerHTML` in this file.
 */
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import {
  ArchiveBoxIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsPointingOutIcon,
  CheckIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { Avatar } from '../ui/Avatar';
import { AssigneePicker } from './AssigneePicker';
import { ExternalAssigneePicker } from './ExternalAssigneePicker';
import { ExternalReviewActions } from './ExternalReviewActions';
import { AuthorReviewActions } from './AuthorReviewActions';
import { TaskStatusPicker } from './TaskStatusPicker';
import type { BoardColumn, TaskCard as TaskCardData } from '../../services/api/board';
import { useUpdateTask } from '../../hooks/useUpdateTask';
import { useDeleteTask } from '../../hooks/useDeleteTask';
import { useArchiveTask } from '../../hooks/useArchiveTask';
import { useTaskMutation } from '../../hooks/useTaskMutation';
import { useMilestones } from '../../hooks/useMilestones';
import { useAssignTaskMilestone } from '../../hooks/useAssignTaskMilestone';
import type { Milestone } from '../../services/api/milestones';
import { BillingPeriodField } from './BillingPeriodField';
import { TaskDueChip } from './TaskDueChip'; // MKS-341
import { TaskTimeBookingLockField } from './TaskTimeBookingLockField'; // MKS-364
import { useAuth } from '../../services/authContext';
import { useFlag } from '../../services/flagsContext';
import { CommentList, type CommentHighlight } from '../comments/CommentList';
import { CommentOrderToggle } from '../comments/CommentOrderToggle'; // MKS-514
import type { BubbleActor } from '../comments/CommentBubble';
import { CommentComposer } from '../comments/CommentComposer';
import { MarkdownBody } from '../comments/MarkdownBody';
import { TaskRefPicker, type TaskRefInsertion } from '../comments/TaskRefPicker';
import { useExternalCurrentUser } from '../../hooks/useExternalCurrentUser';
import { SlaTaskCard } from '../projects/SlaTaskCard';
import { SLA_TASK_TYPE_ID } from '../../constants/typeIds';
// Phase 45 Plan 45-08 (D-14..D-16, Open Q7) — 5 new body sections + the
// mark-read-on-mount mutation. The Phase 41 P17 SHELL (right-anchored panel,
// focus trap, Esc-revert, body scroll lock, backdrop close) stays intact;
// only the body content is extended.
import { AcceptanceCriteriaList } from '../slide-over-sections/AcceptanceCriteriaList';
import { TaskTimeEntries } from '../slide-over-sections/TaskTimeEntries';
import { TaskAttachmentsSection } from '../slide-over-sections/TaskAttachmentsSection';
import { TaskActivityTimeline } from '../slide-over-sections/TaskActivityTimeline';
import { TaskWatchersSection } from '../slide-over-sections/TaskWatchersSection';
import { TaskLinksSection } from '../slide-over-sections/TaskLinksSection'; // MKS-162
import { useMarkTaskRead } from '../../hooks/useMarkTaskRead';
// Phase 49 Plan 49-03 — SLA tier picker in the slide-over header.
import { TaskTierPicker } from './TaskTierPicker';
import { TaskPriorityPicker } from './TaskPriorityPicker'; // MKS-163
import { TaskLabelPicker } from './TaskLabelPicker'; // MKS-164
import { TriagerProposalSection } from './TriagerProposalSection'; // 261002-dfh
import { PendingQuestionsSection } from './PendingQuestionsSection'; // MKS-384
import { useRobotEnabled } from '../../hooks/useRobotEnabled'; // MKS-338
import { useProjectSlaTiers } from '../../hooks/useProjectSlaTiers';
// 260723-cdi — created date + shared internal-only staleness emoji helper.
import {
  formatDisplayDate,
  taskStalenessEmoji,
  DISABLED_TASK_STALENESS_CONFIG,
  type TaskStalenessConfig,
} from '../../utils/format';
// Quick task 260729-gi8 — Copy link header button (shared copy-toast pattern,
// see CreateTokenModal for the canonical usage).
import { useCopyToast } from '../../hooks/useCopyToast';
import { formatDayMonth } from '../../utils/dateFormat';
import { DateField } from '../ui/DateField';

// ---------------------------------------------------------------------------
// Props (mirror TaskDetailModalProps so the KanbanBoardPage swap is 1:1)
// ---------------------------------------------------------------------------

export interface TaskDetailSlideOverProps {
  task: TaskCardData | null;
  audience: 'internal' | 'external';
  isOpen: boolean;
  onClose: () => void;
  onUpdateError?: (message: string) => void;
  onDeleteError?: (message: string) => void;
  /**
   * Phase 30 — when true, the milestone field renders as an editable combobox;
   * when false, as plain read-only text. Computed in KanbanBoardPage (D-10).
   */
  canManageMilestones?: boolean;
  /**
   * MKS-364 — when true, the "Time booking" row gets its Lock / Unlock button.
   * The STATE is shown to every internal reader regardless; this gates only the
   * button, and the backend re-enforces the identical admin-OR-PM gate.
   *
   * A separate prop rather than a reuse of `canManageMilestones` above: the two
   * happen to be the same expression at both call sites today (admin OR the
   * project's PM, exactly as `canReorderColumns` / `canInviteExternal` also are
   * in KanbanBoardPage), but a prop named for milestones must not become the
   * thing that decides who may close a ticket for booking. Defaults to false,
   * so the surfaces that render this slide-over without project context
   * (MyTasksTaskPanel, SlaBreachDashboardPage) show the lock read-only.
   */
  canManageTimeBookingLock?: boolean;
  /**
   * Phase 41 Plan 41-08 (Surface 5d-A) — parent project's `taskTypeId`.
   * When equal to `SLA_TASK_TYPE_ID` the slide-over mounts the inline SLA
   * dual-bar timer (CONTEXT D-4: "inline only — uniform density across
   * kanban + detail"). Per checker I-09 this is an explicit prop forwarded
   * from KanbanBoardPage; never read via `task.project.taskTypeId` (does
   * not exist on TaskCardData).
   */
  projectTaskTypeId?: number | null;
  /**
   * 260913-jpy — per-project staleness indicator config, forwarded from
   * KanbanBoardPage / MyTasksTaskPanel. Omitted means the disabled default
   * at the render call site — this surface never falls back to the
   * always-on global ladder just because a mount point forgot to wire it.
   */
  stalenessConfig?: TaskStalenessConfig;
  /**
   * UAT 2026-05-22 #2 — kanban columns for the parent project, forwarded
   * from KanbanBoardPage. Drives the in-header TaskStatusPicker pill so the
   * task status surfaces on the slide-over with the same colour as on the
   * board. Optional: when omitted (or the data is still loading) the
   * picker simply does not render and the existing header layout is
   * preserved. Same source as the drag/drop swimlanes — no extra fetch.
   */
  columns?: BoardColumn[];
  /**
   * MKS-144 — `'panel'` (default) renders the existing right-anchored
   * overlay (backdrop, dialog semantics, focus trap, scroll lock). `'page'`
   * drops ALL of that overlay chrome because the content owns the whole
   * document at `/tasks/:taskRef` — the body sections (description,
   * acceptance criteria, comments, activity, ...) are rendered identically
   * in both variants; only the wrapper differs.
   */
  variant?: 'panel' | 'page';
  /**
   * MKS-144 — panel-mode-only "Open full screen" affordance. Rendered next
   * to the copy-link button when supplied. A callback prop (not a `<Link>`)
   * on purpose: this component mounts in specs without a Router, and the
   * external portal variant must never surface the internal `/tasks/*`
   * route at all.
   */
  onOpenFullPage?: () => void;
  /**
   * MKS-384 — the `?comment=<id>` of the board deep link, forwarded from
   * KanbanBoardPage. A Slack DM about one Factory™ question links here, so the
   * thread opens scrolled to THAT question's comment instead of at the bottom.
   * Omitted everywhere else, which is today's behaviour.
   */
  deepLinkCommentId?: number | null;
}

type PanelMode = 'view' | 'edit' | 'archive-confirm' | 'delete-confirm';

// Phase 113 (Plan 113-04 / PCSYNC-03) — the Factory™ dispatch-state pill's
// three-branch label/class mapping. Deliberately an explicit lookup over the
// KNOWN three-word vocabulary rather than a fallback that echoes the raw
// status string: a future fifth vocabulary value must render NOTHING (no
// pill) rather than leak a database value into the UI. `dispatch_failed`
// uses a distinct color family (red) from `dispatched` (green) so the two
// are tellable apart at a glance, not only by reading the label text.
const PAPERCLIP_DISPATCH_PILL: Record<string, { label: string; className: string }> = {
  pending: {
    label: 'Dispatch pending',
    className:
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800',
  },
  dispatched: {
    label: 'Dispatched to Factory™',
    className:
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800',
  },
  dispatch_failed: {
    label: 'Dispatch failed',
    className:
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800',
  },
};

const PAPERCLIP_AWAITING_BINDING_PILL: { label: string; className: string; title?: string } = {
  label: 'Awaiting Factory™ binding',
  title:
    'This project is not bound to a Factory™ company, so nothing will dispatch this task. Bind the project in Monk Bot Admin, then Retry it from Factory™ Tasks.',
  className:
    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800',
};

// Phase 130 (Plan 130-11 / D-15, D-17) — why a dispatch is held back until the project's runner is
// ready. Explicit lookup, like the maps around it: an unknown reason renders nothing. Shown only
// with the runtimes release flag on.
const PAPERCLIP_RUNNER_HOLD_PILL: Record<
  string,
  { label: string; className: string; title?: string }
> = {
  runner_building: {
    label: 'Runner building',
    title: "Waiting for this project's runner to finish building",
    className:
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800',
  },
  runner_failed: {
    label: 'Runner failed',
    title: "This project's runner could not be built",
    className:
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800',
  },
};

// Phase 114 (Plan 114-04 / PCEXEC-01, D-114-05) — the Factory™ execution
// run-status pill's five-branch label/class mapping. A SIBLING of the
// dispatch-state pill map above, never an extension or a merge: "did we hand
// it off" (dispatch) and "what is the agent doing" (run) are different facts
// that can legitimately disagree — a dispatched task whose agent never
// started must show BOTH truths as two separate pills. Same explicit-lookup
// discipline as the dispatch map: a future sixth vocabulary value must
// render NOTHING rather than leak a database value into the UI. Colour
// families are chosen so the four non-waiting states are distinguishable at
// a glance AND so `finished` never collides with `dispatched`'s green — a
// dispatched-but-unfinished task must not read as done.
//
// Phase 115 (Plan 115-03 / PCOPS-05, D-115-12) adds the fifth entry,
// `waiting_human`: the agent is blocked on a pending human question in
// Factory™. This is a DELIBERATE, human-confirmed decision
// (Task 1 of 115-03-PLAN.md, a `gate="blocking-human"` checkpoint) that
// `waiting_human` and `running` are MUTUALLY EXCLUSIVE — a task blocked on
// a human question stops reading as `running`. Amber/orange is the free
// colour family (not used by any of the other four entries above, nor by
// `PAPERCLIP_DISPATCH_PILL`'s green).
const PAPERCLIP_RUN_STATUS_PILL: Record<string, { label: string; className: string }> = {
  running: {
    label: 'Factory™ running',
    className:
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800',
  },
  finished: {
    label: 'Factory™ finished',
    className:
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-teal-100 text-teal-800',
  },
  failed: {
    label: 'Factory™ failed',
    className:
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800',
  },
  cancelled: {
    label: 'Factory™ cancelled',
    className:
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-800',
  },
  waiting_human: {
    label: 'Waiting on a human',
    className:
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800',
  },
};

// Parse a free-text number input into a wire value. Empty → null (clears the
// field). Non-numeric / negative → 'invalid' so the caller surfaces an error
// instead of POSTing NaN. Mirrors TaskDetailModal's helper.
function parseOptionalNumber(raw: string): number | null | 'invalid' {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return 'invalid';
  return n;
}

// Selector matching all focusable descendants for the focus-trap loop.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function TaskDetailSlideOver({
  task,
  audience,
  isOpen,
  onClose,
  onUpdateError,
  onDeleteError,
  canManageMilestones = false,
  canManageTimeBookingLock = false, // MKS-364
  projectTaskTypeId,
  stalenessConfig,
  columns,
  variant = 'panel',
  onOpenFullPage,
  deepLinkCommentId = null, // MKS-384
}: TaskDetailSlideOverProps) {
  // MKS-144 — derived before the effects below so their guards can use it
  // (Rules of Hooks: no hook is added/removed/reordered between renders).
  const isPanel = variant === 'panel';
  const [mode, setMode] = useState<PanelMode>('view');
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editBudget, setEditBudget] = useState('');
  const [editHardLimit, setEditHardLimit] = useState(false);
  const [editPlannedHours, setEditPlannedHours] = useState('');
  // MKS-341 — raw `<DateField>` value: a `YYYY-MM-DD` day or '' for
  // "no due date". '' is what the user clearing the field produces, and it maps
  // to an explicit `null` on save (not to "leave it alone").
  const [editDueDate, setEditDueDate] = useState('');
  // 260819-kq3 — `#` task-reference picker on the description editor.
  const descriptionTaRef = useRef<HTMLTextAreaElement | null>(null);

  const updateMutation = useUpdateTask();
  const archiveMutation = useArchiveTask();
  const deleteMutation = useDeleteTask();
  const visibilityMutation = useTaskMutation();
  // Quick task 260729-gi8 — Copy link header button.
  const { copied: linkCopied, copy: copyLink } = useCopyToast();

  // 1:1 actor-resolution from TaskDetailModal. useAuth is unconditional so
  // hook order stays stable; useExternalCurrentUser only fires for external
  // audience (gated via the second arg).
  const { user: internalUser, isAdmin } = useAuth();
  // MKS-338 — no robot / Factory™ add-on, no Factory™ section and no Triager
  // proposal panel. The backend already strips the data both read (the task
  // card DTO's paperclip fields and the `robot-candidate` label), so this is
  // the presentation half of the same gate, not the enforcement.
  const robotEnabled = useRobotEnabled();
  // MKS-384 — which comment the thread jumps to and flashes. Seeded by the
  // `?comment=<id>` deep link out of a Slack DM, re-set by the
  // "N questions waiting" marker. The nonce is what makes a repeat jump to the
  // same comment scroll again (see `CommentHighlight`).
  const [commentHighlight, setCommentHighlight] = useState<CommentHighlight | null>(null);
  // MKS-514 — comment thread order, persisted in the user's preferences.
  // Internal audience only: portal users have no /auth/preferences. External
  // keeps the server's oldest-first order.
  const commentOrderEnabled = audience === 'internal';
  // Phase 130 (Plan 130-11, D-17) — the runner hold pill beside the dispatch pill; flag off: today's block.
  const runtimesEnabled = useFlag('mks_522_monkfactory_runtimes');
  const highlightNonceRef = useRef(0);
  const jumpToComment = useCallback((commentId: number) => {
    highlightNonceRef.current += 1;
    setCommentHighlight({ commentId, nonce: highlightNonceRef.current });
  }, []);
  useEffect(() => {
    if (deepLinkCommentId == null) return;
    jumpToComment(deepLinkCommentId);
  }, [deepLinkCommentId, jumpToComment]);
  const externalQuery = useExternalCurrentUser(audience === 'external');
  const externalUser = externalQuery.data ?? null;

  const bubbleActor: BubbleActor | null =
    audience === 'internal' && internalUser
      ? { kind: 'user', id: internalUser.id, isAdmin }
      : audience === 'external' && externalUser
        ? { kind: 'external_user', id: externalUser.id }
        : null;

  // Reset edit state when task identity / open state changes. Mirrors the
  // TaskDetailModal BLOCKER-2 contract: list primitive fields, not the
  // `task` reference, so cache identity churn does not clobber in-progress
  // edits.
  useEffect(() => {
    setMode('view');
    if (task) {
      setEditName(task.name);
      setEditDescription(task.description ?? '');
      setEditBudget(task.budget !== null ? String(task.budget) : '');
      setEditHardLimit(task.hardLimit);
      setEditPlannedHours(task.plannedHours !== null ? String(task.plannedHours) : '');
      setEditDueDate(task.dueDate ?? ''); // MKS-341
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    task?.id,
    task?.name,
    task?.description,
    task?.budget,
    task?.hardLimit,
    task?.plannedHours,
    task?.dueDate, // MKS-341
    isOpen,
  ]);

  const cancelEdit = useCallback(() => {
    if (task) {
      setEditName(task.name);
      setEditDescription(task.description ?? '');
      setEditBudget(task.budget !== null ? String(task.budget) : '');
      setEditHardLimit(task.hardLimit);
      setEditPlannedHours(task.plannedHours !== null ? String(task.plannedHours) : '');
      setEditDueDate(task.dueDate ?? ''); // MKS-341
    }
    setMode('view');
  }, [task]);

  // ---------------------- Focus trap + Esc ----------------------
  // Mitigates T-41-V3-05. Implemented inline (no @headlessui/react dependency
  // available). On open: capture the previously focused element; focus the
  // first focusable in the panel after one frame so the close button isn't
  // pinged before content mounts. On close: restore focus to the previously
  // focused element. Tab cycles through focusable descendants; Shift+Tab
  // wraps backwards.
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // MKS-144 — page variant owns the whole document; no overlay focus trap
    // to seed/restore (native page tab order is correct).
    if (!isOpen || !isPanel) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    // Defer focus to next paint so the panel is in the DOM tree.
    const raf = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const first = focusables[0];
      if (first) first.focus();
    });

    return () => {
      window.cancelAnimationFrame(raf);
      // Restore focus to whatever opened the panel.
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [isOpen, isPanel]);

  // Document-level Escape listener so Esc fires even when no panel descendant
  // owns focus.
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // Mirror TaskDetailModal: in edit mode Esc reverts; in archive-confirm /
      // delete-confirm Esc returns to view; in view Esc closes the panel
      // entirely.
      if (mode === 'edit') {
        cancelEdit();
      } else if (mode === 'archive-confirm' || mode === 'delete-confirm') {
        setMode('view');
      } else if (isPanel) {
        // MKS-144 — in page mode Esc must NEVER navigate away mid-read; it
        // only reverts edit/archive-confirm/delete-confirm modes above.
        onClose();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, mode, onClose, cancelEdit, isPanel]);

  // Tab-key trap inside the panel. We intercept Tab on the panel container so
  // focus stays inside the slide-over for the lifetime of the open state.
  function handlePanelKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute('disabled')
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Body scroll lock while the panel is open (matches Modal behavior).
  // MKS-144 — page variant is a normal document flow; it must NOT lock
  // body scroll.
  useEffect(() => {
    if (!isOpen || !isPanel) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen, isPanel]);

  // Phase 45 Plan 45-08 (D-13, Open Q7) — mark this task read when the
  // slide-over MOUNTS with a non-null task id. UPSERTs lastReadAt = NOW so
  // the MyTasks row unread badge clears on next refresh. Fires once per
  // (taskId, isOpen) pair — re-firing when a new task is opened in the same
  // component instance is intentional. Silent on error (worst case: stale
  // badge, never blocking).
  const markReadMutation = useMarkTaskRead();
  const markRead = markReadMutation.mutate;
  const taskIdForRead = task?.id ?? null;
  useEffect(() => {
    if (!isOpen) return;
    if (taskIdForRead == null) return;
    markRead(taskIdForRead);
    // markRead reference is stable per TanStack Query mutation contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, taskIdForRead]);

  // Phase 49 Plan 49-03 — SLA tier picker gate. Gate computed before the early
  // return so the hook call order is unconditional (Rules of Hooks).
  // The `task?.tier != null` check avoids fetching tiers for tasks that have
  // never been assigned one (non-SLA or pre-49 tasks).
  const slaTierEnabled =
    projectTaskTypeId === SLA_TASK_TYPE_ID && task?.tier != null && audience === 'internal';
  const tiersQuery = useProjectSlaTiers(slaTierEnabled ? task?.projectId : undefined);

  if (!task || !isOpen) return null;
  const isExternalAudience = audience === 'external';
  // External tasks v1 (260702) — bod 3 (review) surfaces only for the CURRENT
  // external assignee of the task. Backend enforces the same rule (any 4xx →
  // toast); this only gates the affordance client-side. MKS-132: bod 2
  // (reassign) is no longer gated by this — any external member gets the
  // picker below.
  const isCurrentExternalAssignee =
    isExternalAudience &&
    externalUser != null &&
    task.assigneeKind === 'external_user' &&
    task.externalAssigneeId != null &&
    task.externalAssigneeId === externalUser.id;
  // Quick task 260810-l18 — author-only Approve/Reopen gate
  // (Task.createdById / Task.createdByExternalId), independent of
  // membership/assignment. `currentIsTerminal` is hoisted here (rather than
  // recomputed inline where the staleness emoji uses it below) so both call
  // sites share one expression.
  const currentIsTerminal =
    columns?.find((c) => c.taskStateId === task.taskStateId)?.isTerminal ?? false;
  // Phase 113 (Plan 113-04 / PCSYNC-03) — resolved once, here, rather than
  // indexed inline at each JSX use site: `noUncheckedIndexedAccess` types a
  // Record index access as possibly-undefined, and computing it once here
  // both satisfies that and keeps the render below a plain conditional
  // instead of a repeated lookup.
  // A `pending` dispatch refused for want of a project binding will never
  // move on its own — label it for what it is rather than "Dispatch pending"
  // (found in the Phase 115 staging walk).
  const paperclipDispatchPill: { label: string; className: string; title?: string } | undefined =
    task.paperclipAwaitingBinding
      ? PAPERCLIP_AWAITING_BINDING_PILL
      : task.paperclipDispatchStatus
        ? PAPERCLIP_DISPATCH_PILL[task.paperclipDispatchStatus]
        : undefined;
  // Phase 114 (Plan 114-04) — same noUncheckedIndexedAccess reason as the
  // dispatch pill resolution immediately above.
  const paperclipRunStatusPill = task.paperclipRunStatus
    ? PAPERCLIP_RUN_STATUS_PILL[task.paperclipRunStatus]
    : undefined;
  // Phase 130 (Plan 130-11 / D-15, D-17) — resolved once; undefined with the flag off or for no hold.
  const paperclipRunnerHoldPill =
    runtimesEnabled && task.paperclipHoldReason
      ? PAPERCLIP_RUNNER_HOLD_PILL[task.paperclipHoldReason]
      : undefined;
  // Phase 115 (Plan 115-03 / PCOPS-05, D-115-13) — the outbound issue link.
  // Server-derived (`paperclipIssueUrl.ts`); null when the task has no
  // Factory™ issue yet or the dispatch URL is unset. Rendered whenever
  // present, regardless of run status — an operator may want to jump to the
  // issue even for a merely `running` task, not only a `waiting_human` one.
  const paperclipIssueLink = task.paperclipIssueUrl ?? null;
  // MKS-312 (Plan 126-05 / D-19) — the active family's rows. Internal audience only; the
  // server already sends [] to external actors, this is the second gate (T-126-19).
  const factoryFamilyRows = !isExternalAudience ? (task.factoryFamily ?? []) : [];
  const isAuthor =
    (!isExternalAudience && internalUser != null && internalUser.id === task.createdById) ||
    (isExternalAudience && externalUser != null && externalUser.id === task.createdByExternalId);

  // -------------------- Mutations / handlers --------------------

  function handleSaveEdit() {
    if (!task) return;
    const trimmedName = editName.trim();
    if (trimmedName.length === 0 || trimmedName.length > 500) return;
    const newDescription = editDescription.trim() === '' ? null : editDescription.trim();
    const newBudget = parseOptionalNumber(editBudget);
    const newPlannedHours = parseOptionalNumber(editPlannedHours);
    if (newBudget === 'invalid' || newPlannedHours === 'invalid') {
      if (onUpdateError) {
        onUpdateError('Budget and planned hours must be non-negative numbers.');
      }
      return;
    }
    const namedChanged = trimmedName !== task.name;
    const descChanged = newDescription !== (task.description ?? null);
    const budgetChanged = newBudget !== (task.budget ?? null);
    const hardLimitChanged = editHardLimit !== task.hardLimit;
    const plannedChanged = newPlannedHours !== (task.plannedHours ?? null);
    // MKS-341 — '' in the date input means "no due date", which is an explicit
    // null on the wire, NOT an omitted field. Comparing the normalised values
    // is what makes clearing a date a real change rather than a no-op.
    const newDueDate = editDueDate === '' ? null : editDueDate;
    const dueDateChanged = newDueDate !== (task.dueDate ?? null);
    if (
      !namedChanged &&
      !descChanged &&
      !budgetChanged &&
      !hardLimitChanged &&
      !plannedChanged &&
      !dueDateChanged
    ) {
      setMode('view');
      return;
    }
    updateMutation.mutate(
      {
        projectId: String(task.projectId),
        taskId: task.id,
        ...(namedChanged ? { name: trimmedName } : {}),
        ...(descChanged ? { description: newDescription } : {}),
        ...(budgetChanged ? { budget: newBudget } : {}),
        ...(hardLimitChanged ? { hardLimit: editHardLimit } : {}),
        ...(plannedChanged ? { plannedHours: newPlannedHours } : {}),
        ...(dueDateChanged ? { dueDate: newDueDate } : {}), // MKS-341
      },
      {
        onSuccess: () => setMode('view'),
        onError: (err) => {
          if (onUpdateError) onUpdateError(err.message ?? 'Server error');
        },
      }
    );
  }

  function handleConfirmArchive() {
    if (!task) return;
    archiveMutation.mutate(
      { projectId: String(task.projectId), taskId: task.id },
      {
        onSuccess: () => onClose(),
        onError: (err) => {
          if (onDeleteError) onDeleteError(err.message ?? 'Server error');
        },
      }
    );
  }

  function handleConfirmDelete() {
    if (!task) return;
    deleteMutation.mutate(
      { projectId: String(task.projectId), taskId: task.id },
      {
        onSuccess: () => onClose(),
        onError: (err) => {
          if (onDeleteError) onDeleteError(err.message ?? 'Server error');
        },
      }
    );
  }

  // Quick task 260729-gi8 / MKS-144 — build the canonical, audience-correct
  // deep link. External audience keeps the portal-only `?openTask=` form
  // built from the CURRENT origin + pathname (other query params are
  // intentionally dropped, not appended, so stray params never leak into
  // the shared link) — the portal path must keep resolving for external
  // users. Internal audience now copies the permanent full-page URL,
  // `/tasks/{id}`, using the NUMERIC id (not `task.code` — codes are not
  // DB-unique, ids always are). Clipboard writes are best-effort: an
  // unavailable/rejecting clipboard API fails silently.
  function handleCopyLink() {
    if (!task) return;
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    const url =
      audience === 'external'
        ? `${window.location.origin}${window.location.pathname}?openTask=${task.id}`
        : `${window.location.origin}/tasks/${task.id}`;
    copyLink(url).catch(() => {});
  }

  // MKS-198 — open the permanent full-page URL in a new browser tab. Same URL
  // the internal branch of handleCopyLink builds. `noopener,noreferrer` keeps
  // the new tab from reaching back through `window.opener`.
  function handleOpenInNewTab() {
    if (!task) return;
    window.open(`${window.location.origin}/tasks/${task.id}`, '_blank', 'noopener,noreferrer');
  }

  const trimmedName = editName.trim();
  const canSaveEdit =
    trimmedName.length > 0 && trimmedName.length <= 500 && !updateMutation.isPending;
  const canConfirmArchive = !archiveMutation.isPending;
  const canConfirmDelete = !deleteMutation.isPending;

  // -------------------- Time-section derived strings ------------
  // No live timer wiring in Phase 41 (the task-timer hook is a future-phase
  // concern). The static text reflects the available `reportedHours` and
  // `plannedHours` so the visual contract from the design ref is preserved.
  const reported = task.reportedHours ?? 0;
  const planned = task.plannedHours;
  const overEstimatePct =
    planned !== null && planned > 0 && reported > planned
      ? Math.round(((reported - planned) / planned) * 100)
      : null;

  // -------------------- JSX --------------------
  // MKS-144 — header and body are shared VERBATIM between the `panel` and
  // `page` variants; only the outer wrapper (backdrop / dialog semantics /
  // Tab trap vs. a plain full-width container) differs below. Extracted to
  // local consts (not a separate function component — no new hook-order
  // concerns) so neither branch duplicates this markup.
  const header = (
    <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-ms-border shrink-0">
      {mode === 'edit' ? (
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          maxLength={500}
          className="flex-1 text-lg font-semibold text-ms-text-strong px-2 py-1 border border-ms-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
          aria-label="Task name"
        />
      ) : (
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            {task.code && (
              <span className="text-xs font-mono font-semibold uppercase tracking-wide text-ms-text-secondary">
                {task.code}
              </span>
            )}
            {/* UAT 2026-05-22 #2 — status pill + change dropdown. Pill
                    colour mirrors the kanban column for `task.taskStateId`;
                    hidden on external audience (no board mutation rights).

                    MKS-177 — canManage is `true` for every internal viewer
                    (was: `canManageMilestones || isAdmin`, i.e. admin OR PM).
                    That gate was strictly narrower than the authority the
                    board itself grants: a project member may already drag the
                    same card between columns (KanbanSwimlaneCell gates the
                    drop target on audience alone), so the detail view rendered
                    a dead read-only label for a move the user could perform
                    one click away. Same rule and same rationale as the
                    sibling TaskPriorityPicker (MKS-163) and AssigneePicker
                    (MKS-132), and the same `canManage={true}` the picker
                    already gets in ProjectListView: the server owns the
                    permission, and a refusal surfaces as a 403 toast via
                    onError. */}
            {columns && !isExternalAudience && (
              <TaskStatusPicker
                taskId={task.id}
                projectId={task.projectId}
                currentStateId={task.taskStateId}
                columns={columns}
                canManage={true}
                audience="internal"
                onError={onUpdateError}
              />
            )}
            {/* Phase 49 Plan 49-03 — SLA tier picker. Gate: SLA Task project +
                    task has a tier assigned + internal audience + tiers loaded. */}
            {slaTierEnabled && task.tier && tiersQuery.data?.data && (
              <TaskTierPicker
                taskId={task.id}
                projectId={task.projectId}
                audience="internal"
                currentTier={task.tier}
                tiers={tiersQuery.data.data}
                canManage={canManageMilestones || isAdmin}
                onError={onUpdateError}
              />
            )}
            {/* MKS-163 (D-3/D-4) — priority picker, gated on
                !isExternalAudience and nothing else: the permission rule is
                "whoever may PATCH /api/tasks/:id today", same as the pencil
                Edit button below. Default to 'normal' for legacy fixtures
                that pre-date this field. */}
            {!isExternalAudience && (
              <TaskPriorityPicker
                taskId={task.id}
                projectId={task.projectId}
                currentPriority={task.priority ?? 'normal'}
                canManage={true}
                onError={onUpdateError}
              />
            )}
          </div>
          {/* 260722-g8r UAT — wrap instead of truncate: long titles had no
           * way to be read in full anywhere. break-words guards unbroken
           * strings (URLs, test gibberish) from overflowing the panel. */}
          <h2
            id="task-detail-slideover-heading"
            className="text-lg font-semibold text-ms-text-strong break-words"
          >
            {task.name}
          </h2>
        </div>
      )}

      <div className="flex items-center gap-2 shrink-0">
        {/* MKS-132 — assignee picker for EVERY internal viewer (was:
                admin / PM / current assignee, quick task 260708-ml6). The
                backend enforces project membership, so a non-member's attempt
                surfaces as a 403 toast. */}
        {!isExternalAudience ? (
          <AssigneePicker
            taskId={task.id}
            projectId={task.projectId}
            currentAssigneeId={task.assigneeId ?? null}
            currentAssigneeName={task.assigneeName ?? null}
            isExternalTask={task.isExternal}
            currentAssigneeKind={task.assigneeKind ?? null}
            currentAssigneeColorSeq={task.assigneeColorSeq ?? null}
            currentAssigneeAvatarUrl={task.assigneeAvatarUrl ?? null}
            /* MKS-513 — lets the External tab mark the current assignee. */
            currentExternalAssigneeId={task.externalAssigneeId ?? null}
          />
        ) : externalUser ? (
          /* External tasks v1 (260702) bod 2 + MKS-132 — any external
                 member of the project can reassign, to internal users or
                 external members. Backend enforces membership. */
          <ExternalAssigneePicker
            taskId={task.id}
            projectId={task.projectId}
            currentAssigneeId={
              task.assigneeKind === 'external_user'
                ? (task.externalAssigneeId ?? null)
                : (task.assigneeId ?? null)
            }
            currentAssigneeKind={task.assigneeKind ?? null}
            currentAssigneeName={task.assigneeName ?? null}
          />
        ) : (
          task.assigneeName && (
            <div
              className="flex items-center gap-1.5 px-2 py-1 bg-ms-canvas border border-ms-border-subtle rounded-full"
              title={`Assigned to ${task.assigneeName}${task.assigneeKind === 'external_user' ? ' (External)' : ''}`}
              data-testid="task-detail-assignee-chip"
            >
              <Avatar
                name={task.assigneeName}
                colorSeq={task.assigneeColorSeq}
                avatarUrl={task.assigneeAvatarUrl}
                size="xs"
              />
              <span className="text-[11px] font-medium text-ms-text-secondary">
                {task.assigneeName}
              </span>
              {task.assigneeKind === 'external_user' && (
                <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
                  External
                </span>
              )}
            </div>
          )
        )}
        {!isExternalAudience && mode === 'view' && (
          <button
            type="button"
            onClick={() => setMode('edit')}
            title="Edit task"
            aria-label="Edit task"
            className="h-8 w-8 flex items-center justify-center text-ms-text-muted hover:text-ms-text rounded-md hover:bg-ms-canvas focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <PencilSquareIcon className="w-5 h-5" />
          </button>
        )}
        {!isExternalAudience &&
          (task.canFlipVisibility ? (
            <button
              type="button"
              onClick={() => {
                visibilityMutation.mutate(
                  {
                    type: 'visibility',
                    taskId: task.id,
                    projectId: String(task.projectId),
                    audience: 'internal',
                    toIsExternal: !task.isExternal,
                  },
                  {
                    onError: (err) => {
                      if (onUpdateError) onUpdateError(err.message ?? 'Server error');
                    },
                  }
                );
              }}
              disabled={visibilityMutation.isPending}
              title={task.isExternal ? 'Make internal' : 'Make external'}
              aria-label={task.isExternal ? 'Make internal' : 'Make external'}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium shrink-0 transition-colors disabled:opacity-50 ${
                task.isExternal
                  ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                  : 'bg-green-50 text-green-600 hover:bg-green-100'
              }`}
            >
              {task.isExternal ? (
                <EyeSlashIcon className="w-4 h-4" />
              ) : (
                <EyeIcon className="w-4 h-4" />
              )}
              {task.isExternal ? 'External' : 'Internal'}
            </button>
          ) : (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium shrink-0 ${
                task.isExternal ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'
              }`}
            >
              {task.isExternal ? (
                <EyeSlashIcon className="w-4 h-4" />
              ) : (
                <EyeIcon className="w-4 h-4" />
              )}
              {task.isExternal ? 'External' : 'Internal'}
            </span>
          ))}
        {/* MKS-144 — "Open full screen" (panel mode only, and only when
                the caller supplied a callback — external portal callers
                never pass one, so the internal /tasks/* route is never
                surfaced there). Placed immediately before copy-link. */}
        {isPanel && onOpenFullPage && (
          <button
            type="button"
            onClick={onOpenFullPage}
            title="Open full screen"
            aria-label="Open full screen"
            className="h-8 w-8 flex items-center justify-center text-ms-text-muted hover:text-ms-text rounded-md hover:bg-ms-canvas focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <ArrowsPointingOutIcon className="w-5 h-5" />
          </button>
        )}
        {/* MKS-198 — "Open in new tab" (internal audience only, like
                "Open full screen" above: `/tasks/*` is an internal route the
                external portal must never surface). */}
        {!isExternalAudience && (
          <button
            type="button"
            onClick={handleOpenInNewTab}
            title="Open in new tab"
            aria-label="Open in new tab"
            className="h-8 w-8 flex items-center justify-center text-ms-text-muted hover:text-ms-text rounded-md hover:bg-ms-canvas focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <ArrowTopRightOnSquareIcon className="w-5 h-5" />
          </button>
        )}
        {/* Quick task 260729-gi8 / MKS-144 — Copy link (both audiences).
                Internal audience copies the permanent /tasks/{id} URL;
                external audience keeps the portal-only `?openTask=<id>`
                deep link built from the CURRENT origin/pathname. */}
        <button
          type="button"
          onClick={handleCopyLink}
          title={linkCopied ? 'Copied' : 'Copy link to task'}
          aria-label={linkCopied ? 'Copied' : 'Copy link to task'}
          className="h-8 w-8 flex items-center justify-center text-ms-text-muted hover:text-ms-text rounded-md hover:bg-ms-canvas focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          {linkCopied ? (
            <CheckIcon className="w-5 h-5 text-green-600" />
          ) : (
            <ClipboardDocumentIcon className="w-5 h-5" />
          )}
        </button>
        {/* MKS-144 — close X only makes sense for the overlay panel;
                page mode has no "close" affordance (it's a full document). */}
        {isPanel && (
          <button
            type="button"
            onClick={onClose}
            title="Close panel"
            aria-label="Close panel"
            className="h-8 w-8 flex items-center justify-center text-ms-text-muted hover:text-ms-text rounded-md hover:bg-ms-canvas focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );

  const bodyContent = (
    <>
      {/* ---------- Details section ---------- */}
      <section aria-label="Details" className="space-y-3">
        {mode === 'edit' ? (
          <div className="relative">
            <textarea
              ref={descriptionTaRef}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Description (optional, type # to reference a task)"
              rows={4}
              className="w-full text-sm font-medium text-ms-text px-2 py-1 border border-ms-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              aria-label="Task description"
            />
            {/* 260819-kq3 — `#` opens the task-reference picker; picking
                inserts a plain `#CODE` token that MarkdownBody renders as a
                link chip to /tasks/CODE. */}
            <TaskRefPicker
              textareaRef={descriptionTaRef}
              value={editDescription}
              projectId={task.projectId}
              audience={audience}
              excludeTaskId={task.id}
              onInsert={(ins: TaskRefInsertion) => {
                const ta = descriptionTaRef.current;
                const visible = `#${ins.code} `;
                setEditDescription(
                  (v) => v.substring(0, ins.anchorIndex) + visible + v.substring(ins.partialEnd),
                );
                const newCaret = ins.anchorIndex + visible.length;
                window.requestAnimationFrame(() => {
                  ta?.focus();
                  ta?.setSelectionRange(newCaret, newCaret);
                });
              }}
            />
          </div>
        ) : task.description ? (
          <MarkdownBody
            body={task.description}
            className="text-sm text-ms-text leading-relaxed space-y-2"
          />
        ) : (
          <p className="text-sm font-medium text-ms-text-muted italic">No description.</p>
        )}

        {/* MKS-164 — Labels field, its own section directly under
            Description and above Milestone (moved out of the header row,
            which got crowded/clipped by the assignee control). Same
            section markup/spacing conventions as Milestone below: same
            `pt-2 border-t border-ms-border-subtle` wrapper and the same
            small secondary-text label style. Renders the full picker
            (chips + add/remove affordance), not just read-only chips. */}
        {!isExternalAudience && (
          <div className="pt-2 border-t border-ms-border-subtle">
            <label className="block text-xs font-medium text-ms-text-secondary mb-1">Labels</label>
            <TaskLabelPicker
              taskId={task.id}
              projectId={task.projectId}
              currentLabels={task.labels ?? []}
              canManage={true}
              onError={onUpdateError}
            />
          </div>
        )}

        {/* Milestone field — read-only or combobox per canManageMilestones. */}
        <div className="pt-2 border-t border-ms-border-subtle">
          <label className="block text-xs font-medium text-ms-text-secondary mb-1">Milestone</label>
          <MilestoneComboboxField
            taskId={task.id}
            projectId={task.projectId}
            audience={audience}
            currentMilestoneId={task.milestoneId}
            canEdit={canManageMilestones && audience === 'internal' && mode === 'view'}
            onError={(msg) => onUpdateError?.(`Couldn't update milestone: ${msg}`)}
          />
        </div>

        {/* MKS-136 — the billing period this ticket must be delivered for.
            Separate from the Milestone above on purpose: a ticket sits in a
            sprint AND may be tied to a payment. Renders nothing while the
            release flag is off or the project has no billing schedule. */}
        <BillingPeriodField
          taskId={task.id}
          projectId={task.projectId}
          currentBillingPeriodId={task.billingPeriodId}
          canEdit={audience === 'internal' && mode === 'view'}
          onError={(msg) => onUpdateError?.(`Couldn't update billing period: ${msg}`)}
        />

        {/* 260722-g8r — "Created by" row: display-only creator attribution
         * (no picker, no mutation). External pill mirrors the assignee
         * chip's amber styling; legacy tasks (creator NULL) render the
         * same em-dash empty state as Estimate / Budget above. */}
        <div className="pt-2 border-t border-ms-border-subtle">
          <div className="text-xs font-medium text-ms-text-secondary mb-1">Created by</div>
          {task.createdByName ? (
            <div
              className="flex items-center gap-1.5 text-sm font-medium text-ms-text"
              data-testid="task-detail-created-by"
            >
              <span>{task.createdByName}</span>
              {task.createdByKind === 'external_user' && (
                <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
                  External
                </span>
              )}
            </div>
          ) : (
            <p className="text-sm text-ms-text-muted italic" data-testid="task-detail-created-by">
              —
            </p>
          )}
          {/* 260723 — created date (both audiences) + staleness emoji
           * (internal-only via !isExternalAudience). The emoji is keyed off
           * `updatedAt` (💩 >5d, 🪦 >15d with no update) and suppressed for
           * terminal states (Done / Archived / Cancelled / ...), resolved
           * from the current column in `columns`. */}
          {(() => {
            // Quick task 260810-l18 — `currentIsTerminal` is now hoisted
            // above (shared with the isAuthor gate) instead of
            // recomputed here.
            // 260913-jpy — substitute the disabled default when this
            // surface was never wired to a real per-project config, so an
            // unwired mount point (e.g. a future call site) shows nothing
            // rather than falling back to the always-on global ladder.
            const staleEmoji = taskStalenessEmoji(task.lastActivityAt, {
              isTerminal: currentIsTerminal,
              config: stalenessConfig ?? DISABLED_TASK_STALENESS_CONFIG,
            });
            return (
              <div
                className="mt-1 flex items-center gap-1.5 text-sm text-ms-text-secondary"
                data-testid="task-detail-created-date"
              >
                <span>{formatDisplayDate(task.createdAt)}</span>
                {!isExternalAudience && staleEmoji && (
                  <span
                    role="img"
                    aria-label={staleEmoji.label}
                    title={staleEmoji.label}
                    className="text-base leading-none"
                  >
                    {staleEmoji.emoji}
                  </span>
                )}
              </div>
            );
          })()}
        </div>

        {/* MKS-384 — "N questions waiting" marker. With two Factory™ questions
         * pending on one task, this is the LIVE `Q<n>` numbering a reply uses;
         * each entry jumps the thread to that question's own comment. Internal
         * audience and robot add-on; the section itself renders
         * nothing when the task has no pending question (almost always). */}
        {robotEnabled && !isExternalAudience && (
          <PendingQuestionsSection taskId={task.id} onJumpToComment={jumpToComment} />
        )}

        {/* 261002-dfh — Triager proposal control. A task carrying
         * `robot-candidate` that nobody is assigned to is a Factory™
         * Triager proposal awaiting a human. Same admin / PM gate as the
         * tier picker (the server enforces it too) and never shown to an
         * external audience. */}
        {robotEnabled &&
          !isExternalAudience &&
          (canManageMilestones || isAdmin) &&
          task.assigneeId == null &&
          task.externalAssigneeId == null &&
          (task.labels ?? []).some((l) => l.name === 'robot-candidate') && (
            <TriagerProposalSection
              taskId={task.id}
              projectId={task.projectId}
              onError={onUpdateError}
            />
          )}

        {/* 261001-jua — Factory™ section (moved out of the header row,
         * where the three elements wrapped and collided with the assignee
         * chip). Same `pt-2 border-t` field convention as Milestone /
         * Created by above. Holds, in one wrapping row:
         *   - the dispatch-state pill (Phase 113 / PCSYNC-03), incl. the
         *     "Awaiting Factory™ binding" variant;
         *   - the run-status pill (Phase 114 / PCEXEC-01) — NEXT TO, never
         *     instead of, the dispatch pill (D-114-05: dispatched-but-never-
         *     started is a legitimate state);
         *   - the outbound issue link (Phase 115 / PCOPS-05, D-115-13). Its
         *     freshness is bounded by the sweep interval, so the label says
         *     "as of last sweep"; the agent's question text never renders.
         * Same !isExternalAudience gate as before: which executor runs a
         * task is internal operational detail. Renders NOTHING when the task
         * has no Factory™ data (never routed, or a status outside the known
         * vocabulary — see the PAPERCLIP_*_PILL maps above). */}
        {robotEnabled &&
          !isExternalAudience &&
          (paperclipDispatchPill ||
            paperclipRunnerHoldPill ||
            paperclipRunStatusPill ||
            paperclipIssueLink ||
            factoryFamilyRows.length > 0) && (
            <div
              className="pt-2 border-t border-ms-border-subtle"
              data-testid="task-detail-paperclip"
            >
              <div className="text-xs font-medium text-ms-text-secondary mb-1">Factory™</div>
              <div className="flex flex-wrap items-center gap-2">
                {paperclipDispatchPill && (
                  <span
                    className={paperclipDispatchPill.className}
                    title={paperclipDispatchPill.title}
                  >
                    {paperclipDispatchPill.label}
                  </span>
                )}
                {paperclipRunnerHoldPill && (
                  <span
                    className={paperclipRunnerHoldPill.className}
                    title={paperclipRunnerHoldPill.title}
                    data-testid="task-detail-runner-hold"
                  >
                    {paperclipRunnerHoldPill.label}
                  </span>
                )}
                {paperclipRunStatusPill && (
                  <span className={paperclipRunStatusPill.className}>
                    {paperclipRunStatusPill.label}
                  </span>
                )}
                {paperclipIssueLink && (
                  <a
                    href={paperclipIssueLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline"
                  >
                    View in Factory™ (as of last sweep)
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" aria-hidden="true" />
                  </a>
                )}
              </div>
              {/* MKS-312 (Plan 126-05 / D-19, D-20) — one row per repository / manual step of the
               * active family. Flag on, internal audience, at least one row; otherwise nothing
               * (today's block, unchanged). Every agent-sourced string renders as React text; the
               * PR link is built server-side and only an https://github.com/ URL becomes a link. */}
              {factoryFamilyRows.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="task-detail-factory-family">
                  {factoryFamilyRows.map((row, index) => {
                    const runPill = row.runStatus ? PAPERCLIP_RUN_STATUS_PILL[row.runStatus] : undefined;
                    const prIsLink = row.prUrl != null && row.prUrl.startsWith('https://github.com/');
                    return (
                      <li
                        key={`${row.identifier ?? ''}-${row.repository ?? ''}-${index}`}
                        className="flex flex-wrap items-center gap-2 text-xs text-ms-text-primary"
                        data-testid="task-detail-factory-family-row"
                      >
                        <span className="font-medium">
                          {row.repository ?? `Manual step: ${row.title ?? row.identifier ?? ''}`}
                        </span>
                        {runPill && <span className={runPill.className}>{runPill.label}</span>}
                        {row.prNumber != null &&
                          (prIsLink ? (
                            <a
                              href={row.prUrl ?? undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-blue-700 hover:text-blue-900 hover:underline"
                            >
                              #{row.prNumber}
                            </a>
                          ) : (
                            <span>#{row.prNumber}</span>
                          ))}
                        {row.mergedAt && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 font-medium bg-purple-100 text-purple-800">
                            Merged
                          </span>
                        )}
                        {row.deployedAt && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 font-medium bg-green-100 text-green-800">
                            Deployed
                          </span>
                        )}
                        {!row.allowlisted && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 font-medium bg-red-100 text-red-800">
                            Not on the allowed list
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

        {/* Phase 41 Plan 41-08 (Surface 5d-A) — inline SLA dual-bar timer.
         * Same SlaTaskCard component renders on the kanban card AND here
         * (CONTEXT D-4 uniform density). The 41-08 mount point is
         * preserved verbatim — same gate on projectTaskTypeId. */}
        {projectTaskTypeId === SLA_TASK_TYPE_ID && (
          <div
            data-testid="task-detail-slideover-sla-mount"
            className="pt-2 border-t border-ms-border-subtle"
          >
            <SlaTaskCard taskId={task.id} enabled={true} density="inline" />
          </div>
        )}

        {/* Budget / hard-limit / planned-hours — internal-only metadata. */}
        {mode === 'edit' && !isExternalAudience ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-ms-border-subtle">
            <label className="flex flex-col gap-1 text-xs font-medium text-ms-text-secondary">
              Budget
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={editBudget}
                onChange={(e) => setEditBudget(e.target.value)}
                placeholder="—"
                className="text-sm font-medium text-ms-text px-2 py-1 border border-ms-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                aria-label="Budget"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-ms-text-secondary">
              Estimate
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={editPlannedHours}
                onChange={(e) => setEditPlannedHours(e.target.value)}
                placeholder="—"
                className="text-sm font-medium text-ms-text px-2 py-1 border border-ms-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                aria-label="Estimate"
              />
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-ms-text-secondary sm:self-end sm:pb-1">
              <input
                type="checkbox"
                checked={editHardLimit}
                onChange={(e) => setEditHardLimit(e.target.checked)}
                className="h-4 w-4 rounded border-ms-border text-primary-500 focus:ring-primary-400"
                aria-label="Hard limit"
              />
              Hard limit
            </label>
            {/* MKS-341 — the due date, editable by anyone who can edit the
                ticket (this whole edit form is already behind that gate; the
                backend re-enforces it and rejects every external actor). A
                native `<DateField>` so its value IS the YYYY-MM-DD
                calendar day the API wants — no parsing, no timezone question,
                and a keyboard/screen-reader-correct picker for free. Clearing
                it is how a due date is removed. */}
            <label className="flex flex-col gap-1 text-xs font-medium text-ms-text-secondary">
              Due date
              <DateField
                value={editDueDate}
                onChange={setEditDueDate}
                className="text-sm font-medium text-ms-text px-2 py-1 border border-ms-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                aria-label="Due date"
              />
            </label>
          </div>
        ) : !isExternalAudience ? (
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 pt-2 border-t border-ms-border-subtle text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-ms-text-secondary">
                Reported hours
              </dt>
              <dd className="font-medium text-ms-text">{task.reportedHours}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-ms-text-secondary">Estimate</dt>
              <dd
                className={
                  task.plannedHours !== null
                    ? 'font-medium text-ms-text'
                    : 'text-ms-text-muted italic'
                }
              >
                {task.plannedHours !== null ? task.plannedHours : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-ms-text-secondary">Budget</dt>
              <dd
                className={
                  task.budget !== null ? 'font-medium text-ms-text' : 'text-ms-text-muted italic'
                }
              >
                {task.budget !== null ? task.budget : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-ms-text-secondary">Hard limit</dt>
              <dd className="font-medium text-ms-text">{task.hardLimit ? 'Yes' : 'No'}</dd>
            </div>
            {/* MKS-341 — the read-only due date, with the same amber/red chip
                the board card shows so the two surfaces can never disagree
                about whether a ticket is late. The em-dash empty state matches
                Estimate / Budget above. `currentIsTerminal` is the same value
                the header uses, so a finished ticket is never shown as
                overdue here either. */}
            <div>
              <dt className="text-xs uppercase tracking-wide text-ms-text-secondary">Due date</dt>
              <dd>
                {task.dueDate ? (
                  <TaskDueChip dueDate={task.dueDate} isTerminal={currentIsTerminal} />
                ) : (
                  <span className="text-ms-text-muted italic">—</span>
                )}
              </dd>
            </div>
            {/* MKS-364 — the time-booking lock. It lives HERE, in the read-only
                attribute grid, and NOT in the edit form above: the edit form is
                open to every project member (the backend gates PATCH
                /api/tasks/:id with admin OR PM OR any project assignment), while
                the lock is the project MANAGER's call. So the state is shown to
                every internal reader — anyone about to book time deserves to
                know the ticket is closed before the entry is refused — and only
                `canManageTimeBookingLock` gets the button.
                The button is NOT the security boundary: the backend re-checks
                the same gate and would refuse a hand-rolled request. */}
            <TaskTimeBookingLockField
              taskId={task.id}
              projectId={task.projectId}
              locked={task.timeBookingLocked}
              canManage={canManageTimeBookingLock}
              onError={onUpdateError}
            />
          </dl>
        ) : null}
      </section>

      {/* ---------- External review actions (260702 bod 3) ----------
       * Only for the current external assignee: Approve / Return-for-fix.
       * Both reassign the task to the internal PM server-side (no column
       * move in v1). Backend re-enforces the assignee rule. */}
      {isCurrentExternalAssignee && (
        <ExternalReviewActions taskId={task.id} projectId={task.projectId} />
      )}

      {/* ---------- Author Approve/Reopen (quick task 260810-l18) ----------
       * The AUTHOR of a finished task (createdById / createdByExternalId),
       * regardless of project membership or assignment. An author who is
       * ALSO the current external assignee sees both panels — two
       * different capabilities, so neither is suppressed. */}
      {isAuthor && currentIsTerminal && (
        <AuthorReviewActions taskId={task.id} projectId={task.projectId} audience={audience} />
      )}

      {/* ---------- Acceptance criteria section (Phase 45 D-15) ----------
       * Live JSONB array on Task.acceptanceCriteria; each toggle hits a
       * dedicated PATCH endpoint (no full-task replace race). The Phase
       * 41-17 placeholder text is replaced with the real AcceptanceCriteriaList
       * driven by useTaskAcceptance.
       */}
      <AcceptanceCriteriaList taskId={task.id} audience={audience} />

      {/* ---------- Time section ----------
       * Quick task 260512-p6d (2026-05-12): the Start timer + Log time
       * buttons were rendered disabled as a "visual contract" placeholder
       * for a future task-timer phase. Petra reported they confuse users
       * — if it doesn't work, don't show it. Stripped both buttons + the
       * empty "Time entries" placeholder section. Kept only the real stat
       * (reported / planned) plus the over-estimate badge — both source
       * from the existing task row. Wiring task-timer + per-task time
       * entries is a v7.1 backlog item; re-introduce the controls THEN.
       */}
      <section aria-label="Time" className="pt-3 border-t border-ms-border-subtle">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ms-text-secondary mb-2">
          Time
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-ms-text">
            {reported}h logged
            {planned !== null ? ` of ${planned}h estimate` : ''}
          </span>
          {overEstimatePct !== null && (
            <span className="inline-flex items-center rounded-full bg-red-50 text-red-600 px-2 py-0.5 text-xs font-medium">
              {overEstimatePct}% over estimate
            </span>
          )}
        </div>
      </section>

      {/* ---------- NEW (Phase 45 D-14): TaskTimeEntries ----------
       * Read-only feed of Entry rows scoped to this task. Logging
       * continues via the timesheet flow (LogTimePopover on MyTasks);
       * the slide-over is display-only here per design hand-off.
       */}
      <TaskTimeEntries taskId={task.id} />

      {/* ---------- Attachments section (Phase 45 D-16) ----------
       * Task-level attachments (separate from per-comment). The Phase
       * 41-17 "coming soon" placeholder is replaced with the real
       * TaskAttachmentsSection driven by useTaskAttachments.
       */}
      <TaskAttachmentsSection taskId={task.id} />

      {/* ---------- NEW (Phase 45 D-14): TaskWatchersSection ----------
       * Self-watch toggle via /me sugar endpoints. Add/remove of other
       * users requires PM/admin authz at the backend.
       */}
      <TaskWatchersSection taskId={task.id} audience={audience} />

      {/* ---------- NEW (MKS-162): TaskLinksSection ----------
       * Parent/blocks/duplicates/relates links to other tasks. Internal
       * audience only — the component itself returns null for external,
       * mirroring the backend's authenticate-only (never authenticateAny)
       * gate on all three link routes.
       */}
      <TaskLinksSection taskId={task.id} projectId={task.projectId} audience={audience} />

      {/* ---------- Comments section (1:1 from TaskDetailModal) ---------- */}
      <section aria-label="Comments" className="pt-3 border-t border-ms-border-subtle space-y-3">
        {commentOrderEnabled ? (
          <div className="flex items-center gap-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ms-text-secondary">
              Comments
            </h3>
            <CommentOrderToggle />
          </div>
        ) : (
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ms-text-secondary">
            Comments
          </h3>
        )}
        {bubbleActor === null ? (
          <p className="text-sm text-ms-text-muted italic">Loading comments…</p>
        ) : (
          <>
            <CommentList
              taskId={task.id}
              actor={bubbleActor}
              // MKS-384 — the comment the thread jumps to and flashes, if any.
              highlight={commentHighlight}
              orderable={commentOrderEnabled}
            />
            <CommentComposer
              taskId={task.id}
              projectId={task.projectId}
              audience={audience}
              // MKS-339 — values for a canned reply's {requester_name} /
              // {ticket_code}. `createdByName` already resolves
              // external-creator-first on the wire, which is exactly the
              // requester on a client request.
              cannedReplyContext={{
                requesterName: task.createdByName ?? null,
                ticketCode: task.code ?? null,
              }}
            />
          </>
        )}
      </section>

      {/* UAT 2026-05-22 — Activity log moved BELOW comments (Petra batch 2
       * preference). Audit is a secondary surface relative to the
       * conversation thread; pinning it to the bottom matches read order. */}
      <TaskActivityTimeline taskId={task.id} />

      {/* Edit-mode footer: Save + Cancel. */}
      {mode === 'edit' && (
        <div className="flex justify-end gap-2 pt-3 border-t border-ms-border">
          <Button type="button" variant="secondary" onClick={cancelEdit}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSaveEdit}
            disabled={!canSaveEdit}
            isLoading={updateMutation.isPending}
          >
            Save
          </Button>
        </div>
      )}

      {/* Archive + Delete affordances (quick 260825-ik4 D-4). Two distinct
       * actions side by side: Archive is the routine, easier-to-reach action
       * (quieter `secondary` styling); Delete is the one that costs something
       * (`danger` styling + TrashIcon), and never fires without its own
       * confirmation naming what it destroys. */}
      {!isExternalAudience && mode === 'view' && (
        <div className="flex justify-end gap-2 pt-3 border-t border-ms-border">
          <Button type="button" variant="secondary" onClick={() => setMode('archive-confirm')}>
            <ArchiveBoxIcon className="w-4 h-4 mr-1 inline" />
            Archive
          </Button>
          <Button type="button" variant="danger" onClick={() => setMode('delete-confirm')}>
            <TrashIcon className="w-4 h-4 mr-1 inline" />
            Delete
          </Button>
        </div>
      )}

      {mode === 'archive-confirm' && (
        <div className="space-y-3 pt-3 border-t border-ms-border">
          <p className="text-sm font-medium text-ms-text">
            Archive <span className="font-semibold">{task.name}</span>? This is recoverable — the
            task leaves the board, but its title stays reserved.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setMode('view')}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleConfirmArchive}
              disabled={!canConfirmArchive}
              isLoading={archiveMutation.isPending}
            >
              Confirm Archive
            </Button>
          </div>
        </div>
      )}

      {mode === 'delete-confirm' && (
        <div className="space-y-3 pt-3 border-t border-ms-border">
          <p className="text-sm font-medium text-ms-text">
            Delete <span className="font-semibold">{task.name}</span>? Its comments and
            attachments are permanently removed, and the title becomes available again.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setMode('view')}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={!canConfirmDelete}
              isLoading={deleteMutation.isPending}
            >
              Confirm Delete
            </Button>
          </div>
        </div>
      )}
    </>
  );

  // MKS-144 — panel: unchanged overlay markup (backdrop, dialog semantics,
  // focus trap Tab handler); page: a single full-width wrapper with no
  // backdrop, no dialog role/aria-modal, and no Tab-trap handler (native
  // page tab order is correct). `ref={panelRef}` and the heading's
  // `aria-labelledby`/`id` pairing are kept in BOTH so the existing heading
  // query (and the — currently no-op in page mode — focus-trap code) keep
  // working unchanged.
  if (isPanel) {
    return (
      <div
        data-testid="task-detail-slideover-root"
        className="fixed inset-0 z-50"
        role="presentation"
      >
        {/* Backdrop — click closes, mirrors Modal overlay behavior. */}
        <div
          data-testid="task-detail-slideover-backdrop"
          aria-hidden="true"
          onClick={onClose}
          className="fixed inset-0 bg-black/30"
        />

        {/* Right-anchored panel. transition-transform + translate-x animation
         * provide the slide-in feel; the panel is mounted unconditionally
         * inside the isOpen branch so the entry animation runs naturally. */}
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="task-detail-slideover-heading"
          onKeyDown={handlePanelKeyDown}
          className="fixed right-0 top-0 h-screen w-full lg:w-[640px] xl:w-[720px] bg-ms-surface shadow-2xl border-l border-ms-border flex flex-col transform transition-transform duration-200 ease-out translate-x-0"
        >
          {header}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">{bodyContent}</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="task-detail-page-root" ref={panelRef} className="w-full bg-ms-surface">
      {header}
      <div className="px-6 py-5 space-y-6">{bodyContent}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 30 — Milestone combobox (cloned 1:1 from TaskDetailModal so the
// slide-over does not depend on the soon-to-be-deleted modal file).
// ---------------------------------------------------------------------------

interface MilestoneComboboxFieldProps {
  taskId: number;
  projectId: number;
  audience: 'internal' | 'external';
  currentMilestoneId: number | null;
  canEdit: boolean;
  onError?: (message: string) => void;
}

/**
 * Format a YYYY-MM-DD string as `20.11` (day and month, no year). MKS-351
 * routes it through the shared date util, which reads the string as a calendar
 * date and so cannot drift a day in a timezone behind UTC.
 */
function formatDateUTC(dateStr: string): string {
  return formatDayMonth(dateStr);
}

function formatMilestoneDateRange(m: Pick<Milestone, 'startDate' | 'endDate'>): string | null {
  if (!m.startDate && !m.endDate) return null;
  if (m.startDate && m.endDate) {
    return `${formatDateUTC(m.startDate)} – ${formatDateUTC(m.endDate)}`;
  }
  if (m.startDate) return `od ${formatDateUTC(m.startDate)}`;
  return `do ${formatDateUTC(m.endDate!)}`;
}

function MilestoneComboboxField({
  taskId,
  projectId,
  audience,
  currentMilestoneId,
  canEdit,
  onError,
}: MilestoneComboboxFieldProps) {
  // external-board-redirect-login (2026-07-02) — MUST pass `audience` so
  // external viewers use the raw-fetch transport (externalMilestones.ts),
  // never the internal client.ts one. See useMilestones.ts header comment.
  const milestonesQuery = useMilestones(String(projectId), audience);
  const assignMutation = useAssignTaskMilestone();
  const milestones: Milestone[] = milestonesQuery.data ?? [];
  const current = milestones.find((m) => m.id === currentMilestoneId) ?? null;

  if (!canEdit) {
    return current ? (
      <p className="text-sm text-ms-text">{current.name}</p>
    ) : (
      <p className="text-sm">
        <span className="text-ms-text-secondary">No milestone</span>
      </p>
    );
  }

  return (
    <ComboboxBody
      taskId={taskId}
      projectId={projectId}
      audience={audience}
      milestones={milestones}
      isLoading={milestonesQuery.isLoading}
      currentMilestoneId={currentMilestoneId}
      currentName={current?.name ?? ''}
      onAssign={(milestoneId) =>
        assignMutation.mutate(
          { taskId, milestoneId, projectId: String(projectId), audience },
          {
            onError: (err: unknown) => {
              const msg = err instanceof Error ? err.message : 'Unknown error';
              onError?.(msg);
            },
          }
        )
      }
    />
  );
}

interface ComboboxBodyProps {
  taskId: number;
  projectId: number;
  audience: 'internal' | 'external';
  milestones: Milestone[];
  isLoading: boolean;
  currentMilestoneId: number | null;
  currentName: string;
  onAssign: (milestoneId: number | null) => void;
}

function ComboboxBody({
  milestones,
  isLoading,
  currentMilestoneId,
  currentName,
  onAssign,
}: ComboboxBodyProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!open) return;
    function handleDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) setFilter('');
  }, [open]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return milestones;
    return milestones.filter((m) => m.name.toLowerCase().includes(f));
  }, [milestones, filter]);

  const inputValue = open ? filter : currentName;
  const inputClass =
    'w-full rounded-lg border border-ms-border px-3 py-2 pr-8 text-sm bg-ms-surface text-ms-text focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={inputValue}
        onChange={(e) => {
          setFilter(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Select milestone…"
        className={inputClass}
      />
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? 'Close milestone list' : 'Open milestone list'}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-ms-text-muted hover:text-ms-text"
      >
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-10 w-full mt-1 bg-ms-surface border border-ms-border rounded-lg shadow-lg max-h-60 overflow-auto">
          <button
            type="button"
            onClick={() => {
              onAssign(null);
              setOpen(false);
            }}
            className={`w-full px-3 py-2 text-left text-sm hover:bg-primary-50 ${
              currentMilestoneId === null
                ? 'bg-primary-50 text-primary-700'
                : 'text-ms-text-secondary'
            }`}
          >
            No milestone
          </button>

          {isLoading && (
            <div className="px-3 py-2 text-sm text-ms-text-secondary">Loading milestones…</div>
          )}

          {!isLoading && filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-ms-text-secondary">
              No milestones in this project.
            </div>
          )}

          {filtered.map((m) => {
            const dateRange = formatMilestoneDateRange(m);
            const isSelected = m.id === currentMilestoneId;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  onAssign(m.id);
                  setOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-primary-50 flex items-center justify-between gap-2 ${
                  isSelected ? 'bg-primary-50 text-primary-700' : 'text-ms-text'
                }`}
              >
                <span className="truncate">{m.name}</span>
                {dateRange && (
                  <span className="text-xs text-ms-text-secondary shrink-0">{dateRange}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
